import React, { FC, useState } from "react"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from "../common/Tips"

const ConditionBadge: FC<{ label: string }> = ({ label, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: "4px",
    }}
  >
    <div
      style={{
        padding: "2px 4px",
        backgroundColor: "#999999",
        color: "#FFFFFF",
        borderRadius: "4px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
    <div style={{ marginTop: "0.2em" }}>
      {children}
    </div>
  </div>
)

export type LabelingConditionItem = {
  id: number
  number: number
  disabledAt: string | null
  services: { id: number, name: string }[]
  keyword: { operator: string | null, words: string[] }
  filePaths: string[]
  url: { pattern: string | null, words: string[] }
  assignee: {
    teams: { id: number, name: string }[]
    members: { id: number, name: string }[]
  }
  label: { id: number, name: string, code: string, color: string }
  labelGroup: { id: number, name: string, code: string, color: string }
}

type LabelingConditionContentProps = {
  labelingConditions: LabelingConditionItem[]
  defaultOpen?: boolean
  onDeleteCallback: (id: number, name: string) => void
  onToggleEnabledCallback: (id: number, name: string, current: boolean) => void
  onEditCallback?: (condition: LabelingConditionItem) => void
  onAddCallback?: () => void
}

const LabelingConditionContent: FC<LabelingConditionContentProps> = (props) => {
  const {
    children,
    labelingConditions,
    defaultOpen,
    onDeleteCallback,
    onToggleEnabledCallback,
    onEditCallback,
    onAddCallback,
  } = props

  const [isOpen, setIsOpen] = useState(defaultOpen ?? false)

  return (
    <div className="labeling_condition_content">
      <div
        style={{
          border: "solid 1px #CCCCCC",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px",
          backgroundColor: "#f7f7f7",
          minHeight: "40px",
          cursor: "pointer",
        }}
        onClick={() => setIsOpen((prev) => !prev)}
        role="none"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FontAwesomeIcon
            icon={['fas', isOpen ? 'angle-down' : 'angle-right']}
            style={{ width: "16px", textAlign: "center" }}
          />
          <div>{children}</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 8px",
              backgroundColor: labelingConditions.length > 0 ? "#22aa22" : "#999999",
              color: "#FFFFFF",
              borderRadius: "10px",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            条件数
            {' '}
            {`${labelingConditions.length}件`}
          </div>
          <div
            style={{
              padding: "2px 12px",
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#FFFFFF",
              color: "#666666",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {isOpen ? "閉じる" : "開く"}
          </div>
        </div>
      </div>
      {
        isOpen && (
          <div
            className="block-detail"
            style={{
              border: "solid 1px #CCCCCC",
              borderTop: "none",
              padding: "12px",
            }}
          >
            <table className="flex-table labeling_condition_table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>自動仕分け条件</th>
                  <th>仕分け先</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {
                  !labelingConditions.length && (
                    <tr>
                      <td className="no_data">
                        自動仕分け条件が登録されていません
                      </td>
                    </tr>
                  )
                }
                {
                  labelingConditions.map((labelingCondition) => {
                    const enabled = !labelingCondition.disabledAt
                    const conditionLabel = `No.${labelingCondition.number}`
                    return (
                      <tr key={labelingCondition.id}>
                        <td style={{ justifyContent: "flex-start", textAlign: "left" }}>
                          {conditionLabel}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "8px",
                              color: "#666666",
                              textAlign: "left",
                            }}
                          >
                            {
                              labelingCondition.services.length > 0 && (
                                <ConditionBadge label="アプリケーション">
                                  {labelingCondition.services.map((v) => v.name).join(", ")}
                                </ConditionBadge>
                              )
                            }
                            {
                              labelingCondition.keyword.words.length > 0 && (
                                <ConditionBadge label="キーワード">
                                  {`【${labelingCondition.keyword.operator === "or" ? "いずれかを含む" : "全てを含む"}】`}
                                  {labelingCondition.keyword.words.join(", ")}
                                </ConditionBadge>
                              )
                            }
                            {
                              labelingCondition.url.words.length > 0 && (
                                <ConditionBadge label="URL">
                                  {`【${labelingCondition.url.pattern === "exact" ? "完全一致" : "部分一致"}】`}
                                  {labelingCondition.url.words.join(", ")}
                                </ConditionBadge>
                              )
                            }
                            {
                              labelingCondition.filePaths.length > 0 && (
                                <ConditionBadge label="ファイルパス">
                                  {labelingCondition.filePaths.join(", ")}
                                </ConditionBadge>
                              )
                            }
                            {
                              (labelingCondition.assignee.teams.length > 0 || labelingCondition.assignee.members.length > 0) && (
                                <ConditionBadge label="チーム・ユーザー">
                                  {
                                    [
                                      ...labelingCondition.assignee.teams.map((t) => t.name),
                                      ...labelingCondition.assignee.members.map((m) => m.name),
                                    ].join(", ")
                                  }
                                </ConditionBadge>
                              )
                            }
                          </div>
                        </td>
                        <td>
                          <div className="labeling_target_chip">
                            <div className="target_item">
                              <div
                                className="target_color"
                                style={{ backgroundColor: labelingCondition.labelGroup.color }}
                              />
                              {labelingCondition.labelGroup.name}
                              {!!labelingCondition.labelGroup.code && ` (${labelingCondition.labelGroup.code})`}
                            </div>
                            <FontAwesomeIcon icon={['fas', 'angle-right']} style={{ color: "#999999" }} />
                            <div className="target_item">
                              <div
                                className="target_color round"
                                style={{ backgroundColor: labelingCondition.label.color }}
                              />
                              {labelingCondition.label.name}
                              {!!labelingCondition.label.code && ` (${labelingCondition.label.code})`}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Tips description={enabled ? "無効化する" : "有効化する"} className="" icon={false}>
                              <FontAwesomeIcon
                                icon={['fas', enabled ? 'toggle-on' : 'toggle-off']}
                                style={{
                                  color: enabled ? "#008800" : "#666666",
                                  cursor: "pointer",
                                  fontSize: "24px",
                                }}
                                onClick={() => onToggleEnabledCallback(labelingCondition.id, conditionLabel, !enabled)}
                              />
                            </Tips>
                            <Tips description="編集する" className="" icon={false}>
                              <FontAwesomeIcon
                                icon={['fas', 'tools']}
                                style={{ cursor: "pointer", fontSize: "20px", color: "#666666" }}
                                onClick={() => onEditCallback?.(labelingCondition)}
                              />
                            </Tips>
                            <Tips description="削除する" className="" icon={false}>
                              <FontAwesomeIcon
                                icon={['fas', 'trash-alt']}
                                style={{ cursor: "pointer", fontSize: "20px", color: "#FF0000" }}
                                onClick={() => onDeleteCallback(labelingCondition.id, conditionLabel)}
                              />
                            </Tips>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </table>
            {
              onAddCallback && (
                <button
                  type="button"
                  onClick={onAddCallback}
                  style={{
                    display: "inline-block",
                    width: "100%",
                    marginTop: "8px",
                    backgroundColor: "#d6e8d4",
                    border: "1px solid #81b365",
                    color: "#81b365",
                    padding: "12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  &nbsp;
                  追加
                </button>
              )
            }
          </div>
        )
      }
    </div>
  )
}

export default LabelingConditionContent
